import React, { useRef, useState, useEffect } from "react";
import { db, storage } from "./firebase";
import { collection, onSnapshot } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { doc } from "firebase/firestore";
import AIHostTTSPlayer from "./ai/AIHostTTSPlayer";
import TeamNameSash from "./components/TeamNameSash";

// Use AIHostTTSPlayer (Google Cloud TTS) only - no Web Speech fallback
function hostSpeak(text, { rate = 0.95 } = {}) {
  if (typeof window !== "undefined" && typeof window.aiSpeak === "function") {
    window.aiSpeak(text, { rate });
    return true; // spoke via AIHostTTSPlayer
  }
  return false; // Google Cloud TTS not available
}


function HostScoresIntro({
  src = "/video/Character_Spec_for_Pop_Superfan_Host_noaudio.mp4",
  line = "Let's have a look at the current team scores.",
  // AUDIO
  audioDelayMs = -220,
  ttsLatencyMs = 140,
  rate = 1.0,

  // VISUAL TIMING
  fadeMs = 420,
  dollyMs = 600,
  blurMs = 280,

  style = {}
}) {
  const videoRef = React.useRef(null);
  const [videoReady, setVideoReady] = React.useState(false);
  const [ttsReady, setTtsReady] = React.useState(
    typeof window !== "undefined" && typeof window.aiSpeak === "function"
  );
  const [visible, setVisible] = React.useState(false);
  const firedRef = React.useRef(false);

  // Detect when AIHostTTSPlayer has registered window.aiSpeak
  React.useEffect(() => {
    if (ttsReady) return;
    let tries = 0;
    const id = setInterval(() => {
      if (typeof window.aiSpeak === "function") {
        setTtsReady(true);
        clearInterval(id);
      } else if (++tries > 200) {
        clearInterval(id); // ~10s
      }
    }, 50);
    return () => clearInterval(id);
  }, [ttsReady]);

  // Prime video so play() is instant and first frame is snappy
  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.playsInline = true;
    v.preload = "auto";

    // Warm up: play → pause
    v.play().then(() => { v.pause(); setVideoReady(true); }).catch(() => {});

    const onCanPlay = () => setVideoReady(true);
    const onLoadedData = () => { if (v.readyState >= 3) setVideoReady(true); };
    const onPlaying = () => setVisible(true);
    const onPause = () => setVisible(false);
    const onEnded = () => setVisible(false);

    v.addEventListener("canplay", onCanPlay);
    v.addEventListener("loadeddata", onLoadedData);
    v.addEventListener("playing", onPlaying);
    v.addEventListener("pause", onPause);
    v.addEventListener("ended", onEnded);
    return () => {
      v.removeEventListener("canplay", onCanPlay);
      v.removeEventListener("loadeddata", onLoadedData);
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("ended", onEnded);
    };
  }, []);

  // Helper: speak now
  const speakNow = React.useCallback(() => hostSpeak(line, { rate }), [line, rate]);

  const playVideo = React.useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    try { v.currentTime = 0; } catch {}
    v.play().catch(() => {});
  }, []);

  // Start both once ready, compensating for TTS latency
  React.useEffect(() => {
    if (firedRef.current) return;
    if (!videoReady || !ttsReady) return;

    const D = Number(audioDelayMs) || 0;         // requested offset
    const L = Math.max(0, Number(ttsLatencyMs)); // expected TTS start latency

    if (D === 0) {
      speakNow();
      setTimeout(playVideo, L);
    } else if (D > 0) {
      playVideo();
      const delay = Math.max(0, D - L);
      setTimeout(speakNow, delay);
    } else {
      const wait = L + Math.abs(D);
      speakNow();
      setTimeout(playVideo, wait);
    }

    firedRef.current = true;
  }, [videoReady, ttsReady, audioDelayMs, ttsLatencyMs, speakNow, playVideo]);

  const enterScale = visible ? 1 : 0.96;

  return (
    <video
      ref={videoRef}
      src={src}
      muted
      playsInline
      preload="auto"
      style={{
        opacity: visible ? 1 : 0,
        transform: `scale(${enterScale})`,
        filter: visible ? "blur(0px)" : "blur(2px)",
        transformOrigin: "bottom left",
        transition: `opacity ${fadeMs}ms ease-out, transform ${dollyMs}ms cubic-bezier(.2,.7,.2,1), filter ${blurMs}ms ease-out`,
        willChange: "opacity, transform, filter",
        position: "fixed",
        left: 20,
        bottom: 20,
        width: 840,
        zIndex: 20000,
        borderRadius: 12,
        boxShadow: "0 10px 40px rgba(0,0,0,.5)",
        ...style
      }}
    />
  );
}

// Glow for the card border based on rank
const getGlow = (rank) => {
  if (rank === 0) return "0 0 32px 8px #ffd700, 0 0 0 7px #fff8";
  if (rank === 1) return "0 0 32px 8px #c0c0c0, 0 0 0 7px #fff8";
  if (rank === 2) return "0 0 32px 8px #cd7f32, 0 0 0 7px #fff8";
  return "0 12px 64px #000b, 0 0 0 7px #fff4";
};

// Helper for '1st', '2nd', etc.
const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"],
    v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const buildPlaceMaps = (scoresAsc) => {
  const desc = scoresAsc.slice().reverse();
  const countByScore = new Map();
  desc.forEach(([, points]) => {
    countByScore.set(points, (countByScore.get(points) || 0) + 1);
  });

  const placeByTeam = new Map();
  const tieByTeam = new Map();
  let prevScore = null;
  let prevPlace = 0;
  desc.forEach(([team, points], i) => {
    const place = points === prevScore ? prevPlace : i + 1;
    placeByTeam.set(team, place);
    tieByTeam.set(team, (countByScore.get(points) || 0) > 1);
    prevScore = points;
    prevPlace = place;
  });

  return { placeByTeam, tieByTeam };
};

// Rough TTS duration estimate so we pace reads to finish before the next one
function estimateTtsMs(text, rate = 0.95) {
  const words = (String(text).trim().match(/\b[\w’'-]+\b/g) || []).length;
  const baseWps = 2.7;           // words/second at rate=1.0 (conservative)
  const wps = Math.max(1.2, baseWps * rate);
  const secs = Math.max(0.9, words / wps); // min ~0.9s so very short lines still breathe
  return Math.round(secs * 1000) + 180;    // + latency/padding
}

// ADD: compact leaderboard summary (still used elsewhere if needed)
function summarizeTop(scoresAsc, count = 3) {
  const top = scoresAsc.slice().reverse().slice(0, count);
  if (!top.length) return "";
  const parts = top.map(([team, pts], i) =>
    i === 0 ? `${team} on ${pts}` : `${i + 1 === 2 ? "then" : "and"} ${team} on ${pts}`
  );
  return parts.join(", ");
}

export default function Scores({ gameCode, numQuestions, allTeamNames = [] }) {
  const [answers, setAnswers] = useState([]);
  const [scores, setScores] = useState([]);
  const [teamInfo, setTeamInfo] = useState({});
  const [bgUrl, setBgUrl] = useState(null);
  const [isEndRound, setIsEndRound] = useState(false);

  const scrollRef = useRef(null);
  const [rowWidth, setRowWidth] = useState(0);
  // Track previous snapshot to detect leader changes and movers
  const prevScoresRef = useRef(new Map());
  const mountedRef = useRef(false);

  // Speak-on-show & guards
  const introSpokenRef = useRef(false);
  const lastSpokenHashRef = useRef("");

  // ——— LOADERS ———

  useEffect(() => {
    // Measure one full cycle (teams + gap slots); content is duplicated for the loop.
    if (scrollRef.current) {
      const fullSetWidth = scrollRef.current.scrollWidth / 2;
      setRowWidth(fullSetWidth);
    }
  }, [scores.length]);

  // Background image from first quiz question
  useEffect(() => {
    if (!gameCode) return;
    const unsub = onSnapshot(doc(db, "games", gameCode), (snap) => {
      const data = snap.data();
      const currentIdx = typeof data?.current === "number" ? data.current : null;
      const currentQ = currentIdx !== null ? data?.questions?.[currentIdx] : null;
      const rawType = String(
        currentQ?.type || currentQ?.CAT || currentQ?.TYPE || ""
      );
      const normalizedType = rawType.toUpperCase().replace(/\s+/g, "");
      setIsEndRound(normalizedType === "ENDROUND");

      const first = data?.questions?.[0];
      const raw = first?.BACKGROUND || first?.background;
      if (!raw) return;

      if (raw.startsWith("http")) {
        setBgUrl(raw);
      } else {
        getDownloadURL(ref(storage, "media/" + raw.trim()))
          .then(setBgUrl)
          .catch((err) => {
            console.error("Failed to load background image:", err);
          });
      }
    });
    return unsub;
  }, [gameCode]);

  // Listen for answers
  useEffect(() => {
    if (!gameCode) return;
    const unsub = onSnapshot(
      collection(db, "games", gameCode, "answers"),
      (snap) => {
        const all = [];
        snap.forEach((doc) => all.push(doc.data()));
        setAnswers(all);
      }
    );
    return unsub;
  }, [gameCode]);

  // Listen for teams & their selfies
  useEffect(() => {
    if (!gameCode) return;
    const unsub = onSnapshot(
      collection(db, "games", gameCode, "teams"),
      (snap) => {
        const info = {};
        snap.forEach((doc) => {
          const data = doc.data();
          info[data.team] = data.selfieUrl || null;
        });
        setTeamInfo(info);
      }
    );
    return unsub;
  }, [gameCode]);

  // Calculate team scores (ascending: lowest..highest)
  useEffect(() => {
    const currentTeams = new Set(Object.keys(teamInfo));
    const scoreByTeam = {};

    answers.forEach((a) => {
      if (!currentTeams.has(a.team)) return;
      if (!scoreByTeam[a.team]) scoreByTeam[a.team] = 0;
      scoreByTeam[a.team] += typeof a.points === "number" ? a.points : 0;
    });

    // Baseline to 0 so teams show even without answers yet
    Object.keys(teamInfo).forEach((team) => {
      if (!Object.prototype.hasOwnProperty.call(scoreByTeam, team)) {
        scoreByTeam[team] = 0;
      }
    });

    const sorted = Object.entries(scoreByTeam)
      .filter(([, score]) => !isNaN(score))
      .sort((a, b) => a[1] - b[1]);

    setScores(sorted);
  }, [answers, teamInfo]);

  // ——— ANNOUNCE UPDATES (kept) ———
  useEffect(() => {
    if (!scores.length) return;

    // Current maps
    const currentMap = new Map(scores); // team -> points
    const currentOrderAsc = scores.map(([team]) => team);      // lowest..highest
    const currentRank = new Map(currentOrderAsc.map((t, i) => [t, i]));

    // Previous maps
    const prevMap = prevScoresRef.current;
    const prevOrderAsc = Array.from(prevMap.entries())
      .sort((a, b) => a[1] - b[1])
      .map(([t]) => t);
    const prevRank = new Map(prevOrderAsc.map((t, i) => [t, i]));

    // Compute movers (teams that improved rank)
    const movers = [];
    for (const [team] of scores) {
      const was = prevRank.has(team) ? prevRank.get(team) : null;
      const now = currentRank.get(team);
      if (was !== null && was !== undefined && now !== null && now !== undefined) {
        const delta = was - now; // positive = moved up
        if (delta > 0) movers.push({ team, delta });
      }
    }
    movers.sort((a, b) => b.delta - a.delta);

    // Leader is last item in ascending array
    const [leaderTeam, leaderPoints] = scores[scores.length - 1] || [];

    // Broadcast to any host/orchestrator listener
    try {
      window.dispatchEvent(
        new CustomEvent("score:update", {
          detail: {
            leaderboard: scores
              .slice()
              .reverse()
              .map(([team, points]) => ({ team, points })),
            movers, // [{team, delta}]
          },
        })
      );
    } catch (_) {}

    // Lightweight spoken feedback, but not on first mount
    if (mountedRef.current) {
      const prevLeader = prevOrderAsc.length ? prevOrderAsc[prevOrderAsc.length - 1] : null;
      const leaderChanged = leaderTeam && leaderTeam !== prevLeader;

      // Avoid talking over our top-5 readout snapshot
      const justSpokenSameSnapshot = lastSpokenHashRef.current === JSON.stringify(scores);

      if (!justSpokenSameSnapshot) {
        if (leaderChanged) {
          window.aiSpeak?.(`Leaderboard update. ${leaderTeam} now leads on ${leaderPoints} points.`);
        } else if (movers.length) {
          const topMovers = movers.slice(0, 3).map((m) => m.team);
          window.aiSpeak?.(`Movement on the ladder: ${topMovers.join(", ")} climbing the board.`);
        }
      }
    } else {
      mountedRef.current = true;
    }

    // Save snapshot for next comparison
    prevScoresRef.current = currentMap;
  }, [scores]);

  // ——— Top-5 readout: early start + duration-based pacing ———
// ——— Top-5 readout: early start + duration-based pacing ———
    /*
    useEffect(() => {
      if (!scores || scores.length === 0) return;

      // De-dupe by snapshot so we don't double schedule
      const snapshot = JSON.stringify(scores);
      if (lastSpokenHashRef.current === snapshot) return;

      // Build top-N lines (<=5), in reverse with best last
      const n = Math.min(5, scores.length);
      const topAsc = scores.slice(-n); // lowest among top-N first
      const lines = topAsc.map(([team, pts], i) => {
        const rank = n - i;
        if (rank === 1) return `And leading: ${team} on ${pts} points.`;
        const overallRankWord = ordinal(rank);
        return `In ${overallRankWord} place: ${team} on ${pts} points.`;
      });

      // Start almost immediately; don't wait for the scroll loop
      const speechRate = 0.92;             // a touch slower for clarity
      const leadInMs = 400;                // small visual settle
      const minGapMs = 1600;               // never tighter than this
      const timers = [];

      // Chain the reads based on estimated durations so each finishes cleanly
      let atMs = leadInMs;
      lines.forEach((line) => {
        const dur = Math.max(minGapMs, estimateTtsMs(line, speechRate));
        const id = setTimeout(() => {
          hostSpeak(line, { rate: speechRate });
        }, atMs);
        timers.push(id);
        atMs += dur;
      });

      // Mark snapshot as spoken immediately to avoid re-scheduling mid-read
      lastSpokenHashRef.current = snapshot;

      return () => {
        timers.forEach(clearTimeout);
      };
    }, [scores]);
    */

  const MARQUEE_GAP_SLOTS = 4;

  // One cycle: last→1st, then empty gap slots, duplicated for seamless infinite scroll
  const marqueeItems = React.useMemo(() => {
    const cycle = [
      ...scores.map((entry) => ({ type: "team", entry })),
      ...Array.from({ length: MARQUEE_GAP_SLOTS }, (_, i) => ({
        type: "gap",
        id: i,
      })),
    ];
    return [...cycle, ...cycle];
  }, [scores]);

  const { placeByTeam, tieByTeam } = React.useMemo(
    () => buildPlaceMaps(scores),
    [scores]
  );

  const CARD_WIDTH = "18vw";
  const CARD_HEIGHT = "80vh";
  const CARD_GAP = "2vw";
  const marqueeDurationSec = Math.max(
    8,
    (scores.length + MARQUEE_GAP_SLOTS) * 2.6
  );

  return (
    <div
      style={{
        width: "100vw",
        height: "100vh",
        minHeight: "100vh",
        background: bgUrl
          ? `url("${bgUrl}") center center / cover no-repeat`
          : "#111",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        fontFamily: "sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Mount once so window.aiSpeak works on this screen */}
      <AIHostTTSPlayer
        defaultVoice="Google UK English Male"
        defaultSpeed={0.95}
        style={{ display: "none" }}
      />

      {/* Heading */}
      <div
        style={{
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          backdropFilter: "blur(8px)",
          border: "3px solid #fff",
          borderRadius: "1.2em",
          padding: "0.1em 0.1em",
          marginTop: 8,
          marginBottom: 8,
          width: "90%",
          maxWidth: 800,
          fontSize: "3rem",
          fontWeight: 700,
          color: "#fff",
          textAlign: "center",
          boxShadow: "0 2px 18px 0 #0008",
        }}
      >
        🏆 Final Scores
      </div>

      {/* Host intro video + line (kept) */}
      <HostScoresIntro />

      {/* Marquee/Scroll Area */}
      <div
        style={{
          width: "100vw",
          flex: 1,
          height: "100%",
          overflow: "visible",
          position: "relative",
          display: "flex",
          alignItems: "stretch",
        }}
      >
        <div
          className="scroll-row"
          ref={scrollRef}
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            height: CARD_HEIGHT,
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            gap: CARD_GAP,
            animation: rowWidth
              ? `marquee-scroll ${marqueeDurationSec}s linear infinite`
              : "none",
            "--marquee-width": rowWidth ? `${rowWidth}px` : "0px",
          }}
        >
          {marqueeItems.map((item, idx) => {
            if (item.type === "gap") {
              return (
                <div
                  key={`gap-${idx}`}
                  aria-hidden="true"
                  style={{
                    height: "100%",
                    width: CARD_WIDTH,
                    minWidth: CARD_WIDTH,
                    maxWidth: CARD_WIDTH,
                    flexShrink: 0,
                    background: "transparent",
                  }}
                />
              );
            }

            const [team, score] = item.entry;
            const selfieUrl = teamInfo[team];
            const place = placeByTeam.get(team) ?? 1;
            const isTied = tieByTeam.get(team) ?? false;
            const medalRank = Math.max(0, place - 1);

            return (
              <div
                key={team + idx}
                style={{
                  height: "100%",
                  aspectRatio: "2/3",
                  width: CARD_WIDTH,
                  minWidth: CARD_WIDTH,
                  maxWidth: CARD_WIDTH,
                  borderRadius: "2.2em",
                  background: "rgba(0,0,0,0.80)",
                  boxShadow: getGlow(medalRank),
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  position: "relative",
                  overflow: "visible",
                }}
              >
                {/* Rank badge */}
                <div
                  style={{
                    position: "absolute",
                    top: -85,
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 10,
                    width: 170,
                    height: 170,
                    borderRadius: "50%",
                    background:
                      medalRank === 0
                        ? "radial-gradient(circle at 38% 32%, #fffbe9 10%, #ffe066 40%, #ffd700 70%, #b8860b 100%)"
                        : medalRank === 1
                        ? "radial-gradient(circle at 38% 32%, #fff 18%, #dbdbdb 60%, #c0c0c0 90%)"
                        : medalRank === 2
                        ? "radial-gradient(circle at 40% 36%, #fdf6e3 10%, #e6b98a 40%, #b87333 74%, #61320b 100%)"
                        : "radial-gradient(circle, #f8f9fa 20%, #cccccc99 100%)",
                    border:
                      medalRank === 0
                        ? "8px solid #ffd700"
                        : medalRank === 1
                        ? "8px solid #c0c0c0"
                        : medalRank === 2
                        ? "8px solid #b67a3a"
                        : "8px solid #bbb9",
                    boxShadow:
                      "0 18px 36px #0007, 0 0 0 14px #fff5 inset, 0 8px 30px #ffe77b99, 0 -2px 20px #fff9 inset",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 900,
                    fontSize: 54,
                    color: "#2d2207",
                    fontFamily: "'Rubik', 'Segoe UI', 'Arial Black', sans-serif",
                    overflow: "visible",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 34,
                      left: 42,
                      width: 78,
                      height: 18,
                      background: "linear-gradient(90deg, #fff9 20%, #fff1 90%)",
                      borderRadius: "24px 24px 40px 40px",
                      opacity: 0.7,
                      pointerEvents: "none",
                      filter: "blur(1px)",
                      zIndex: 2,
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: 88,
                      left: 60,
                      width: 46,
                      height: 14,
                      background: "linear-gradient(90deg, #fff9 50%, #fff1 100%)",
                      borderRadius: "14px 30px 18px 24px",
                      opacity: 0.4,
                      pointerEvents: "none",
                      filter: "blur(0.7px)",
                      zIndex: 2,
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: 24,
                      left: 22,
                      width: 120,
                      height: 32,
                      background: "radial-gradient(ellipse at center, #d7c13766 55%, transparent 100%)",
                      borderRadius: "50%",
                      opacity: 0.47,
                      pointerEvents: "none",
                      filter: "blur(2.5px)",
                    }}
                  />
                  <span style={{ fontSize: "3.2rem", fontWeight: 900 }}>
                    {ordinal(place)}{isTied ? "=" : ""}
                  </span>
                </div>

                {/* Confetti for leader */}
                {place === 1 && (
                  <div className="confetti-container">
                    {Array.from({ length: 18 }).map((_, i) => (
                      <div key={i} className={`confetti confetti${i % 6}`} />
                    ))}
                  </div>
                )}

                <TeamNameSash
                  teamName={team}
                  place={place}
                  selfieUrl={selfieUrl}
                  containerStyle={{ height: "70%", flexShrink: 0 }}
                />

                {/* Score pill */}
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginTop: "auto",
                    marginBottom: 24,
                  }}
                >
                  <div
                    style={{
                      minWidth: 120,
                      minHeight: 70,
                      background: "linear-gradient(90deg, #fffbe2 5%, #ffe066 80%)",
                      border: "5px solid #ffe066",
                      borderRadius: "1.8em",
                      boxShadow: "0 6px 24px #ffd700aa",
                      color: "#8e7600",
                      fontWeight: 900,
                      fontSize: "3.6rem",
                      textAlign: "center",
                      padding: "0.3em 1em",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {typeof score === "number" ? score : ""}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Keyframes and confetti styles */}
      <style>
        {`
        @keyframes marquee-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(calc(-1 * var(--marquee-width))); }
        }

        .scroll-row { will-change: transform; }

        @media (max-width: 1000px) {
          .scroll-row > div {
            width: 200px !important;
            min-width: 200px !important;
            max-width: 200px !important;
            height: 320px !important;
          }
          .scroll-row > div > div:first-child { height: 160px !important; }
          .scroll-row > div > div:nth-child(2),
          .scroll-row > div > div:nth-child(3) {
            font-size: 1.2rem !important;
          }
        }

        @keyframes badge-pulse {
          0% { box-shadow: 0 0 32px 8px #ffd70088; }
          100% { box-shadow: 0 0 48px 18px #ffd700cc; }
        }

        @keyframes confetti-fall {
          0%   { opacity: 1; transform: translateY(0px) rotateZ(0deg);}
          100% { opacity: 0; transform: translateY(95vh) rotateZ(360deg);}
        }

        .confetti-container {
          position: absolute;
          left: 0; 
          top: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 99;
        }

        .confetti {
          position: absolute;
          width: 12px; height: 18px;
          border-radius: 2px;
          opacity: 0.8;
          top: 12px;
          animation: confetti-fall 1.3s linear infinite;
        }
        .confetti0 { left: 12%; background: #ffd700; animation-delay: .0s;}
        .confetti1 { left: 24%; background: #e74c3c; animation-delay: .2s;}
        .confetti2 { left: 37%; background: #00e4d7; animation-delay: .35s;}
        .confetti3 { left: 48%; background: #8e44ad; animation-delay: .13s;}
        .confetti4 { left: 66%; background: #ff55ff; animation-delay: .41s;}
        .confetti5 { left: 79%; background: #ff9800; animation-delay: .15s;}
        `}
      </style>
    </div>
  );
}
